import React, { useEffect } from 'react';
import {
  Box, Typography, useTheme,
} from '@mui/material';
import { lightBlue } from '@mui/material/colors';
import { useTranslation } from 'react-i18next';
import CheckIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { ReactComponent as PaypalLogo } from '../../../assets/images/paypal_logo.svg';
import { ReactComponent as BankTransferLogo } from '../../../assets/images/bank_transfer_logo.svg';
import { ReactComponent as ChequeLogo } from '../../../assets/images/cheque.svg';
import {
  selectActivatePaymentMethodstate,
  selectGetPaymentMethodState,
  selectSetPaymentMethodstate, selectSetPayPalAccountIdState, selectUnlinkPaymentMethodState,
  UpdatePayPalSetAsDefault,
} from '../../../store/slices/dashboardSlice';
import { TestIds } from '../../../assets/testIds';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { WalletSmallInfoCardSkeleton } from '../../../common/WalletSmallInfoCardSkeleton';
import { useCustomEventTracking } from '../../../common/useCustomEventTracking';
import { ListViewButton } from '../../../common/ListViewButton';
import { ManagePayoutSettingsDialog } from './ManagePayoutSettingsDialog';
import { PaymentMethodType } from '../../../common/models/PaymentMethod';
import { PaymentMethodDetails } from './Common';
import { PaymentMethodStatus } from '../../../common/enums';

type PayoutSettingsProps = {
  onLoadingChange?: (loading: boolean) => void;
  canEditPayoutMethod?: boolean;
};

export function PayoutSettings({
  onLoadingChange,
  canEditPayoutMethod = true,
}: PayoutSettingsProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const unlinkPaypalState = useAppSelector(selectUnlinkPaymentMethodState);
  const setPaymentMethodstate = useAppSelector(selectSetPaymentMethodstate);
  const updatePaypalState = useAppSelector(selectSetPayPalAccountIdState);
  const activatePaymentMethodstate = useAppSelector(selectActivatePaymentMethodstate);
  const getPaymentMethodstate = useAppSelector(selectGetPaymentMethodState);
  const { trackPayoutSetting } = useCustomEventTracking();
  const [openDialog, setOpenDialog] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const paymentMethodDetails = PaymentMethodDetails();
  const dispatch = useAppDispatch();

  useEffect(() => {
    // loading state
    const isLoading =
      updatePaypalState === 'pending' ||
      unlinkPaypalState === 'pending' ||
      getPaymentMethodstate === 'pending' ||
      activatePaymentMethodstate === 'pending' ||
      setPaymentMethodstate === 'pending';

    setLoading(isLoading);
    onLoadingChange?.(isLoading);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    unlinkPaypalState,
    getPaymentMethodstate,
    setPaymentMethodstate,
    activatePaymentMethodstate,
    updatePaypalState,
  ]);

  const managePayoutSettingsBtnClick = () => {
    trackPayoutSetting('Click', 'ManagePayout');
    dispatch(UpdatePayPalSetAsDefault(false));
    setOpenDialog(true);
  };

  const getLogoByType = (logoType: string) => {
    switch (logoType) {
      case PaymentMethodType.PAYPAL:
        return <PaypalLogo />;
      case PaymentMethodType.BANK_ACCOUNT:
        return <BankTransferLogo />;
      case PaymentMethodType.PAPER_CHEQUE:
        return <ChequeLogo />;
      default:
        return null;
    }
  };

  const PaymentMethodCard = ({ type }: { type: string }) => {
    const methodDetails =
      paymentMethodDetails[type as keyof typeof paymentMethodDetails];
    return (<>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: theme.spacing(2),
          alignSelf: 'stretch',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            padding: `${theme.spacing(1)} ${theme.spacing(1.25)} ${theme.spacing(1)} ${theme.spacing(1.25)}`,
            justifyContent: 'center',
            alignItems: 'center',
            borderRadius: theme.spacing(12.5),
            background: lightBlue[100],
            height: theme.spacing(5),
            width: theme.spacing(5),
          }}
        >
          {getLogoByType(methodDetails?.type)}
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: theme.spacing(0.625),
            flex: '1 0 0',
          }}
        >
          <Typography
            variant='h15'
            sx={{
              [theme.breakpoints.down('sm')]: {
                fontSize: theme.spacing(1.75),
              },
            }}
          >
            {methodDetails.name}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: theme.spacing(0.625),
              alignSelf: 'stretch',
            }}
          >
            {
              (methodDetails?.isActive &&
                methodDetails?.status === PaymentMethodStatus.VERIFIED) &&
              <CheckIcon color="success" fontSize="small" />
            }
            {
              methodDetails?.status === PaymentMethodStatus.INVALID &&
              <ErrorIcon color="error" fontSize="small" />
            }
            <Typography
              variant='body8'
              sx={{
                [theme.breakpoints.down('sm')]: {
                  fontSize: theme.spacing(1.75),
                  wordBreak: 'break-all',
                  overflowWrap: 'break-word',
                  whiteSpace: 'normal',
                },
              }}
            >
              {
                (methodDetails?.isActive &&
                  methodDetails?.status === PaymentMethodStatus.VERIFIED) &&
                `${t('button.active')} · `
              }
              {
                methodDetails?.status === PaymentMethodStatus.INVALID &&
                `${t('button.invalid')} · `
              }
              {methodDetails.account}
            </Typography>
          </Box>
        </Box>
      </Box>
    </>
    );
  };

  const NoPaymentMethod = () => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.625),
        alignSelf: 'stretch',
      }}
    >
      <ErrorIcon color="error" fontSize="small" />
      <Typography
        data-testid={TestIds.walletPage.payoutNotConnected}
        variant='body1'
      >
        {t('wallet.noPaymentMethods')}
      </Typography>
    </Box >
  );

  return (
    loading ? <WalletSmallInfoCardSkeleton /> : <Box
      data-testid={TestIds.walletPage.payoutSettings}
      sx={{
        display: 'flex',
        padding: theme.spacing(3.125),
        flexDirection: 'column',
        alignItems: 'flex-start',
        flex: '1 0 0',
        gap: theme.spacing(3.125),
        borderRadius: theme.spacing(1.5),
        border: `1px solid ${theme.palette.grey[200]}`,
        [theme.breakpoints.down('sm')]: {
          padding: `${theme.spacing(1.875)} ${theme.spacing(1)}`,
        },
      }}
    >
      <Typography
        variant='h2'
        sx={{
          [theme.breakpoints.down('sm')]: {
            fontSize: theme.spacing(1.75),
          },
        }}
      >
        {t('wallet.payoutSettings')}
      </Typography>

      {/*
        if no payment method exist or
        payment method exist and accounts are unverified
      */}
      {
        (
          (!paymentMethodDetails.bank_account.paymentMethodExist ||
            paymentMethodDetails.bank_account.status === PaymentMethodStatus.UNVERIFIED) &&
          (!paymentMethodDetails.paypal.paymentMethodExist ||
            paymentMethodDetails.paypal.status === PaymentMethodStatus.UNVERIFIED) &&
          (!paymentMethodDetails.paper_cheque.paymentMethodExist ||
            paymentMethodDetails.paper_cheque.status === PaymentMethodStatus.UNVERIFIED)
        ) && <NoPaymentMethod />
      }

      {/*
        only if paypal account exist. Shown alongside a paper check, not replaced by
        it: the Payout Methods chart on "SN Logic on the Edge platform" gives every
        tier ACH and PayPal, and adds paper check for an Association-or-higher member
        whose company has had a rebate paid by check (SN-996).
      */}
      {
        paymentMethodDetails.paypal.paymentMethodExist &&
        paymentMethodDetails.paypal.status !== PaymentMethodStatus.UNVERIFIED &&
        <PaymentMethodCard
          type={PaymentMethodType.PAYPAL}
        />
      }

      {/* only if bank account exist */}
      {
        paymentMethodDetails.bank_account.paymentMethodExist &&
        paymentMethodDetails.bank_account.status !== PaymentMethodStatus.UNVERIFIED &&
        <PaymentMethodCard
          type={PaymentMethodType.BANK_ACCOUNT}
        />
      }

      {/* only if paper cheque exists and is active (not UNLINKED or UNVERIFIED) */}
      {/* Follow same pattern as ACH/PayPal - UNLINKED don't show here (SN-1063) */}
      {
        paymentMethodDetails.paper_cheque.paymentMethodExist &&
        paymentMethodDetails.paper_cheque.status !== PaymentMethodStatus.UNVERIFIED &&
        paymentMethodDetails.paper_cheque.status !== PaymentMethodStatus.UNLINKED &&
        <PaymentMethodCard
          type={PaymentMethodType.PAPER_CHEQUE}
        />
      }
      {/* SN-190: sub-accounts granted read-only Wallet access can't edit payout methods */}
      {canEditPayoutMethod &&
        <ListViewButton
          onBtnClick={managePayoutSettingsBtnClick}
          listViewId={TestIds.walletPage.payoutSettings}
          btnLabel={t('button.managePayoutSettings')}
        />}
      {openDialog && canEditPayoutMethod &&
        <ManagePayoutSettingsDialog
          open={openDialog}
          setOpenDialog={setOpenDialog}
        />}
    </Box >
  );
}
